import React, { useEffect, useMemo, useState } from "react"
import { apiUrl } from "../../api"
import "./weatherradar.css"

const fallbackRadar = {
     provider: "AccuWeather",
     locationName: "Vibo Marina, Calabria",
     radarEnabled: false,
     apiAvailable: false,
     radarUrls: [],
     satelliteUrls: [],
     frameCount: 1,
     frameMinutes: 10,
     locationPosition: {
          latitude: 38.717,
          longitude: 16.128,
     },
     publicRadarUrl: "https://www.accuweather.com/en/it/vibo-marina/1544189/weather-radar/1544189",
}

const DISPLAY_MS = 30000
const INTERVAL_MS = 120000
const FRAME_MS = 850
const PRELOAD_MS = 5000
const STATUS_REFRESH_MS = 7 * 60 * 1000
const OSM_ZOOM = 9
const TILE_SIZE = 256
const MAP_VISUAL_SCALE = 0.9
const DISPLAY_TILE_SIZE = TILE_SIZE * MAP_VISUAL_SCALE

function frameLabel(frameIndex, frameCount, frameMinutes) {
     if (frameCount <= 1) {
          return "Now"
     }

     const minutes = frameIndex * frameMinutes
     return minutes === 0 ? "Now" : `-${minutes} min`
}

function locationPosition(position) {
     const latitude = Number(position?.latitude)
     const longitude = Number(position?.longitude)
     return {
          latitude: Number.isFinite(latitude) ? latitude : 38.717,
          longitude: Number.isFinite(longitude) ? longitude : 16.128,
     }
}

function clamp(value, min, max) {
     return Math.min(max, Math.max(min, value))
}

function lonToPixel(longitude, zoom) {
     return ((longitude + 180) / 360) * TILE_SIZE * Math.pow(2, zoom)
}

function latToPixel(latitude, zoom) {
     const safeLatitude = clamp(latitude, -85.05112878, 85.05112878)
     const radians = (safeLatitude * Math.PI) / 180
     const mercator = Math.log(Math.tan(radians) + 1 / Math.cos(radians))
     return ((1 - mercator / Math.PI) / 2) * TILE_SIZE * Math.pow(2, zoom)
}

function osmTiles(position) {
     const { latitude, longitude } = locationPosition(position)
     const centerX = lonToPixel(longitude, OSM_ZOOM)
     const centerY = latToPixel(latitude, OSM_ZOOM)
     const baseX = Math.floor(centerX / TILE_SIZE)
     const baseY = Math.floor(centerY / TILE_SIZE)
     const maxTile = Math.pow(2, OSM_ZOOM)
     const tiles = []

     for (let y = baseY - 3; y <= baseY + 3; y++) {
          if (y < 0 || y >= maxTile) {
               continue
          }
          for (let x = baseX - 4; x <= baseX + 4; x++) {
               const wrappedX = ((x % maxTile) + maxTile) % maxTile
               const leftPx = (x * TILE_SIZE - centerX) * MAP_VISUAL_SCALE
               const topPx = (y * TILE_SIZE - centerY) * MAP_VISUAL_SCALE
               tiles.push({
                    key: `${OSM_ZOOM}-${wrappedX}-${y}`,
                    z: OSM_ZOOM,
                    x: wrappedX,
                    y,
                    src: `https://tile.openstreetmap.org/${OSM_ZOOM}/${wrappedX}/${y}.png`,
                    left: `${leftPx.toFixed(2)}px`,
                    top: `${topPx.toFixed(2)}px`,
                    leftPx,
                    topPx,
                    size: `${DISPLAY_TILE_SIZE}px`,
               })
          }
     }

     return tiles
}

function mapBounds(mapTiles) {
     if (!mapTiles.length) {
          return {
               left: "0px",
               top: "0px",
               width: "0px",
               height: "0px",
          }
     }

     const left = Math.min(...mapTiles.map((tile) => tile.leftPx))
     const top = Math.min(...mapTiles.map((tile) => tile.topPx))
     return {
          left: `${left.toFixed(2)}px`,
          top: `${top.toFixed(2)}px`,
          width: `${(9 * DISPLAY_TILE_SIZE).toFixed(2)}px`,
          height: `${(7 * DISPLAY_TILE_SIZE).toFixed(2)}px`,
     }
}

function WeatherRadar() {
     const [radar, setRadar] = useState(fallbackRadar)
     const [visible, setVisible] = useState(false)
     const [frame, setFrame] = useState(0)

     useEffect(() => {
          window.dispatchEvent(new CustomEvent("casa-giu-radar-visibility", { detail: { visible } }))
          return () => window.dispatchEvent(new CustomEvent("casa-giu-radar-visibility", { detail: { visible: false } }))
     }, [visible])

     useEffect(() => {
          let mounted = true
          const loadRadar = () => {
               fetch(apiUrl("/controller/accuweather/radar"), { cache: "no-store" })
                    .then((response) => response.json())
                    .then((data) => {
                         if (mounted) {
                              setRadar({ ...fallbackRadar, ...data })
                         }
                    })
                    .catch(() => {
                         if (mounted) {
                              setRadar(fallbackRadar)
                         }
                    })
          }

          loadRadar()
          const refresh = window.setInterval(loadRadar, STATUS_REFRESH_MS)
          return () => {
               mounted = false
               window.clearInterval(refresh)
          }
     }, [])

     useEffect(() => {
          if (!radar.radarEnabled) {
               setVisible(false)
               return undefined
          }

          const show = () => {
               setFrame(0)
               setVisible(true)
               window.setTimeout(() => setVisible(false), DISPLAY_MS)
          }

          const preloadTimeout = window.setTimeout(show, PRELOAD_MS)
          const interval = window.setInterval(show, INTERVAL_MS)
          return () => {
               window.clearTimeout(preloadTimeout)
               window.clearInterval(interval)
          }
     }, [radar.radarEnabled])

     const frames = useMemo(() => {
          const count = Number(radar.frameCount)
          const frameCount = Number.isFinite(count) ? Math.max(0, Math.min(count, 36)) : 0
          return radar.apiAvailable ? Array.from({ length: frameCount }, (_, index) => frameCount - 1 - index) : []
     }, [radar.apiAvailable, radar.frameCount])

     useEffect(() => {
          if (!visible || frames.length <= 1) {
               return undefined
          }

          const interval = window.setInterval(() => {
               setFrame((current) => (current + 1) % frames.length)
          }, FRAME_MS)
          return () => window.clearInterval(interval)
     }, [frames.length, visible])

     if (!radar.radarEnabled) {
          return null
     }

     const compositeFrames = Boolean(radar.compositeFrames)
     const accuweatherFrameCount = Math.max(radar.radarUrls?.length || 0, radar.satelliteUrls?.length || 0)
     const hasAccuweatherFrames = radar.apiAvailable && !compositeFrames && accuweatherFrameCount > 0
     const hasRadarTiles = radar.apiAvailable && ((compositeFrames && frames.length > 0) || hasAccuweatherFrames)
     const currentFrame = frames[frame % Math.max(frames.length, 1)] || 0
     const frameMinutes = Number.isFinite(Number(radar.frameMinutes)) ? Number(radar.frameMinutes) : 10
     const currentLabel = frameLabel(currentFrame, Math.max(frames.length, 1), frameMinutes)
     const mapTiles = osmTiles(radar.locationPosition)
     const radarFrameBounds = mapBounds(mapTiles)
     const radarCacheVersion = radar.updatedAt || 0

     return (
          <section className={`weatherradaroverlay ${visible ? "radarvisible" : "radarhidden"}`} aria-hidden={!visible} aria-label="AccuWeather Radar">
               <div className="radarcard">
                    <div className="radarbar">
                         <div>
                              <span>{radar.provider}</span>
                              <strong>Weather Radar</strong>
                         </div>
                         <em>{radar.locationName}</em>
                    </div>
                    <div className="radarstage">
                         <div className="osmmap" aria-hidden="true">
                              {mapTiles.map((tile) => (
                                   <img
                                        alt=""
                                        className="osmtile"
                                        key={tile.key}
                                        loading="eager"
                                        src={tile.src}
                                        style={{ left: `calc(50% + ${tile.left})`, top: `calc(50% + ${tile.top})`, width: tile.size, height: tile.size }}
                                   />
                              ))}
                              <span className="osmattribution">© OpenStreetMap contributors</span>
                         </div>
                         {compositeFrames && hasRadarTiles ? (
                              <div className="openweatherradartiles openweatherrainfalltiles" aria-hidden="true">
                                   {frames.map((frameIndex, index) => (
                                        <img
                                             alt=""
                                             className={`openweatherradarframe ${index === frame % Math.max(frames.length, 1) ? "active" : ""}`}
                                             key={`rain-frame-${frameIndex}`}
                                             loading="eager"
                                             src={apiUrl(`/controller/accuweather/radar-frame?frame=${frameIndex}&cache=${radarCacheVersion}`)}
                                             style={{
                                                  left: `calc(50% + ${radarFrameBounds.left})`,
                                                  top: `calc(50% + ${radarFrameBounds.top})`,
                                                  width: radarFrameBounds.width,
                                                  height: radarFrameBounds.height,
                                             }}
                                        />
                                   ))}
                              </div>
                         ) : hasAccuweatherFrames ? (
                              <div className="accuweatherradartiles" aria-hidden="true">
                                   {frames.map((frameIndex, index) => (
                                        <img
                                             alt=""
                                             className={`accuweatherradarframe ${index === frame % Math.max(frames.length, 1) ? "active" : ""}`}
                                             key={`accuweather-frame-${frameIndex}`}
                                             loading="eager"
                                             src={apiUrl(`/controller/accuweather/radar-image?kind=radar&index=${frameIndex}&cache=${radarCacheVersion}`)}
                                        />
                                   ))}
                              </div>
                         ) : (
                              <span className="radarbase" aria-hidden="true"></span>
                         )}
                         <div className={`radarui ${hasRadarTiles ? "apiimageui" : "fallbackmapui"}`} aria-hidden="true">
                              <span className="radarsea"></span>
                              <span className="radarland radarlandone"></span>
                              <span className="radarland radarlandtwo"></span>
                              <span className="radarland radarlandthree"></span>
                              <span className="radarroad radarroadone"></span>
                              <span className="radarroad radarroadtwo"></span>
                              <span className="radarcell radarcellone"></span>
                              <span className="radarcell radarcelltwo"></span>
                              <span className="radarcell radarcellthree"></span>
                              <span className="radarcell radarcellfour"></span>
                              <span className="city cityone">Lamezia Terme</span>
                              <span className="city citytwo">Catanzaro</span>
                              <span className="city citythree">Messina</span>
                              <span className="locationpin"></span>
                              <span className="locationlabel">Casa Giu</span>
                         </div>
                         <div className="radarlegend" aria-hidden="true">
                              <span><i className="rainkey"></i>Rain</span>
                              <span><i className="snowkey"></i>Snow</span>
                              <span><i className="icekey"></i>Ice</span>
                              <span><i className="mixkey"></i>Mix</span>
                         </div>
                         <div className="radartimeline" aria-hidden="true">
                              <span className="playbutton"></span>
                              <span className="timeline">
                                   <i style={{ left: `${frames.length > 1 ? (frame / (frames.length - 1)) * 100 : 100}%` }}></i>
                              </span>
                              <strong>{currentLabel}</strong>
                         </div>
                    </div>
                    {!hasRadarTiles && <small className="radarnote">API-Bild nicht verfuegbar</small>}
               </div>
          </section>
     )
}

export default WeatherRadar
